'use client';

import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      el.tabIndex !== -1,
  );
}

interface Options {
  /** When false the trap detaches and focus is released. */
  active?: boolean;
  /** Escape handler. Omit to leave Escape to the caller. */
  onEscape?: () => void;
  /**
   * Focus target when the trap engages. Receives the container so a caller can
   * prefer a specific control; return null to fall back to the first focusable
   * element. Runs a frame late so portaled content is mounted.
   */
  initialFocus?: (root: HTMLElement) => HTMLElement | null | undefined;
  /** Set false to leave focus where it is on teardown. */
  restoreFocus?: boolean;
  /**
   * Opt-in: keep keyboard focus inside even where Tab alone cannot. Tab /
   * Shift+Tab from the container itself (a programmatic fallback) or from
   * <body> goes to the first / last control, and focus that reaches the page
   * behind without a pointer (Safari's default Tab skips buttons, so the
   * first/last wrap never fires there) is sent back in. Stands aside while
   * another modal is open, nested inside or on top.
   */
  contain?: boolean;
  /**
   * Opt-in: element to restore to when nothing usable was focused at open
   * (the opener was disabled mid-request, so focus sat on <body>). When set,
   * restoring uses preventScroll and is skipped if focus has already moved
   * somewhere outside the container.
   */
  restoreFallback?: () => HTMLElement | null | undefined;
}

/**
 * Confines Tab cycling to `ref` and restores focus to whatever was focused
 * before the trap engaged. Extracted from Modal, which is still its largest
 * consumer — an overlay that does not do this leaves a keyboard user tabbing
 * through the page behind it with no visible cursor.
 */
/** An open `aria-modal` other than `root` and its ancestors. */
function hasOtherOpenModal(root: HTMLElement): boolean {
  return Array.from(
    document.querySelectorAll<HTMLElement>('[aria-modal="true"]'),
  ).some(
    (el) => !el.contains(root) && el.getClientRects().length > 0,
  );
}

export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  {
    active = true,
    onEscape,
    initialFocus,
    restoreFocus = true,
    contain = false,
    restoreFallback,
  }: Options = {},
): void {
  // Read when the trap engages and tears down; held in refs so callers can
  // pass inline functions without re-running the effect or going stale.
  const initialFocusRef = useRef(initialFocus);
  const restoreFallbackRef = useRef(restoreFallback);
  useEffect(() => {
    initialFocusRef.current = initialFocus;
    restoreFallbackRef.current = restoreFallback;
  });
  const hasRestoreFallback = !!restoreFallback;

  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const focusRaf = requestAnimationFrame(() => {
      const preferred =
        initialFocusRef.current?.(root) ?? getFocusableElements(root)[0];
      preferred?.focus();
    });

    let lastPointerDown = 0;
    let lastTabShift = false;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onEscape) {
        event.stopPropagation();
        onEscape();
        return;
      }
      if (event.key !== 'Tab') return;
      lastTabShift = event.shiftKey;
      if (contain && hasOtherOpenModal(root)) return;

      const focusable = getFocusableElements(root);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const activeEl = document.activeElement;

      if (
        contain &&
        (!activeEl || activeEl === root || activeEl === document.body)
      ) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && activeEl === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeEl === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onPointerDown = () => {
      lastPointerDown = Date.now();
    };

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || root.contains(target)) return;
      // A click elsewhere is the user's choice; only keyboard focus is held.
      if (Date.now() - lastPointerDown < 1000) return;
      if (hasOtherOpenModal(root)) return;
      const focusable = getFocusableElements(root);
      const next = lastTabShift ? focusable[focusable.length - 1] : focusable[0];
      (next ?? root).focus();
    };

    document.addEventListener('keydown', onKeyDown);
    if (contain) {
      document.addEventListener('pointerdown', onPointerDown, true);
      document.addEventListener('focusin', onFocusIn);
    }
    return () => {
      cancelAnimationFrame(focusRaf);
      document.removeEventListener('keydown', onKeyDown);
      if (contain) {
        document.removeEventListener('pointerdown', onPointerDown, true);
        document.removeEventListener('focusin', onFocusIn);
      }
      if (!restoreFocus) return;
      const fallback = restoreFallbackRef.current;
      if (!fallback) {
        // preventScroll: the opener may sit in a pinned/sticky scroll section,
        // where scrolling it into view jumps the page back.
        previouslyFocused?.focus?.({ preventScroll: true });
        return;
      }
      const current = document.activeElement;
      if (current && current !== document.body && !root.contains(current)) {
        return;
      }
      const target =
        previouslyFocused &&
        previouslyFocused !== document.body &&
        previouslyFocused.isConnected
          ? previouslyFocused
          : fallback();
      if (target?.isConnected) target.focus({ preventScroll: true });
    };
  }, [active, ref, onEscape, restoreFocus, contain, hasRestoreFallback]);
}
